import React, { useEffect, useMemo, useState } from 'react';
import TeamUsagePanel from '../components/TeamUsagePanel';
import TeamActivityPanel from '../components/TeamActivityPanel';
import DashboardConfigPanel from '../components/DashboardConfigPanel';
import SsoPanel from '../components/SsoPanel';
import {
  fetchEnterpriseProfile,
  listTeamMembers, addTeamMember, updateTeamMember, resetTeamMemberPassword, deleteTeamMember, bulkTeamAction,
  listRoles, createRole, updateRole, deleteRole, assignMemberRole,
  TeamMember, TeamMemberPayload, EnterpriseRole,
  FeatureKey, ALL_FEATURES, FEATURE_LABELS,
} from '../../../api/enterpriseApi';
import { Eyebrow, PageHeading } from '../../../design-system';
import {
  User, Home, Search, MessageSquare, History, ClipboardList, Mail,
  BarChart3, Map, Scale, Folder, Shield, Users,
} from 'lucide-react';
import styles from './EnterpriseTeamPage.module.css';

// ── Avatar helpers ────────────────────────────────────────────────────────────
// Initials avatars — each person gets a deterministic on-brand colour based on name hash
const AVATAR_STYLES = [
  { bg: 'var(--ds-info-bg)', border: 'var(--primary-200)', fg: 'var(--primary-800)' },
  { bg: 'var(--ds-success-bg)', border: 'var(--ds-success)', fg: 'var(--ds-success)' },
  { bg: 'var(--ds-info-bg)', border: 'var(--primary-200)', fg: 'var(--ds-brand-blue)' },
  { bg: 'var(--ds-danger-bg)', border: '#fecdca', fg: 'var(--ds-danger)' },
  { bg: 'var(--ds-info-bg)', border: 'var(--primary-300)', fg: 'var(--ds-info)' },
  { bg: 'var(--ds-success-bg)', border: '#a6f4c5', fg: 'var(--ds-success)' },
  { bg: 'var(--ds-info-bg)', border: 'var(--primary-200)', fg: 'var(--primary-800)' },
  { bg: 'var(--ds-info-bg)', border: 'var(--ds-info-bg)', fg: 'var(--ds-brand-blue)' },
];

function nameHash(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return Math.abs(h);
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  if (parts.length === 1 && parts[0].length > 0) return parts[0].slice(0, 2).toUpperCase();
  return '';
}

function AvatarSVG({ name, size = 40 }: { name: string; size?: number }) {
  const style = AVATAR_STYLES[nameHash(name) % AVATAR_STYLES.length];
  const text = initials(name);
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: style.bg, border: `2px solid ${style.border}`, color: style.fg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.4, fontWeight: 600, lineHeight: 1, boxSizing: 'border-box',
    }}>
      {text || <User size={size * 0.5} />}
    </div>
  );
}

const FEATURE_ICONS: Record<FeatureKey, React.ReactNode> = {
  overview: <Home size={14} />, search: <Search size={14} />, chat: <MessageSquare size={14} />,
  'chat-history': <History size={14} />, regulations: <ClipboardList size={14} />,
  newsletters: <Mail size={14} />, charts: <BarChart3 size={14} />, map: <Map size={14} />,
  'search-history': <History size={14} />, comparison: <Scale size={14} />,
  'asset-repository': <Folder size={14} />, governance: <Shield size={14} />, team: <Users size={14} />,
};

// Features that are children of another — auto-toggled with parent
const FEATURE_CHILDREN: Partial<Record<FeatureKey, FeatureKey>> = {
  'chat-history': 'chat',
  'search-history': 'search',
};

const FEATURE_GROUP_LABEL: Partial<Record<FeatureKey, string>> = {
  'chat-history': '↳ included with Chat',
  'search-history': '↳ included with Search',
};

export default function EnterpriseTeamPage() {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkNote, setBulkNote] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [copiedPasswordForId, setCopiedPasswordForId] = useState<number | null>(null);
  const [resettingPasswordForId, setResettingPasswordForId] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [maxSeats, setMaxSeats] = useState<number | null>(null);

  // Roles. `roleId` is the selection in the add/edit form; assigning is a
  // separate call from saving the member, so it is tracked separately.
  const [roles, setRoles] = useState<EnterpriseRole[]>([]);
  const [showRoles, setShowRoles] = useState(false);
  const [assigningRoleFor, setAssigningRoleFor] = useState<number | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [email, setEmail] = useState('');
  const [position, setPosition] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [allowedFeatures, setAllowedFeatures] = useState<FeatureKey[]>([...ALL_FEATURES]);
  const [roleId, setRoleId] = useState<number | null>(null);

  useEffect(() => {
    Promise.all([
      listTeamMembers().catch(() => [] as TeamMember[]),
      fetchEnterpriseProfile().catch(() => null),
      // Roles are optional context: a failure here must not blank the team list,
      // which is the page's actual purpose.
      listRoles().catch(() => [] as EnterpriseRole[]),
    ])
      .then(([teamMembers, profile, roleList]) => {
        setMembers(teamMembers);
        setMaxSeats(profile?.max_seats ?? null);
        setRoles(roleList);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const resetForm = () => {
    setName(''); setEmployeeId(''); setEmail(''); setPosition('');
    setTags([]); setTagInput('');
    setAllowedFeatures([...ALL_FEATURES]);
    setRoleId(null);
    setEditingId(null);
    setFormError(null);
  };

  /** The role currently chosen in the add/edit form, if any. */
  const selectedRole = roleId === null ? null : (roles.find(r => r.id === roleId) ?? null);

  const activeSeatsUsed = members.filter((member) => member.is_active).length;
  const seatLimitReached = maxSeats !== null && activeSeatsUsed >= maxSeats;
  const seatLimitMessage = maxSeats !== null
    ? `This company is limited to ${maxSeats} active member${maxSeats === 1 ? '' : 's'}. You can’t add more right now — contact your account manager to increase the limit.`
    : 'You can’t add more members right now.';

  const openAdd = () => {
    if (seatLimitReached) {
      setFormError(seatLimitMessage);
      setShowForm(false);
      return;
    }
    resetForm();
    setShowForm(true);
  };

  const openEdit = (m: TeamMember) => {
    setName(m.name);
    setEmployeeId(m.employee_id || '');
    setEmail(m.email);
    setPosition(m.position || '');
    setTags((m as any).tags || []);
    setTagInput('');
    setAllowedFeatures([...m.allowed_features]);
    setRoleId(m.role_id ?? null);
    setEditingId(m.id);
    setShowForm(true);
  };

  const addTag = () => {
    const v = tagInput.trim();
    if (v && !tags.includes(v)) setTags(prev => [...prev, v]);
    setTagInput('');
  };

  const toggleFeature = (f: FeatureKey) => {
    setAllowedFeatures(prev => {
      const next = prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f];
      // Auto-add/remove children (chat-history with chat, search-history with search)
      if (f === 'chat') {
        return next.includes('chat') ? (next.includes('chat-history') ? next : [...next, 'chat-history' as FeatureKey]) : next.filter(x => x !== 'chat-history');
      }
      if (f === 'search') {
        return next.includes('search') ? (next.includes('search-history') ? next : [...next, 'search-history' as FeatureKey]) : next.filter(x => x !== 'search-history');
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim() || !email.trim()) return;
    if (editingId === null && seatLimitReached) {
      setFormError(seatLimitMessage);
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload: TeamMemberPayload = {
        name: name.trim(),
        email: email.trim(),
        employee_id: employeeId.trim() || undefined,
        position: position.trim() || undefined,
        allowed_features: allowedFeatures,
      };
      // The role is a separate endpoint from the member record, so it is applied
      // after the save succeeds. Only when it actually changed — reassigning the
      // same role on every edit would be a pointless write.
      const applyRole = async (member: TeamMember): Promise<TeamMember> => {
        if ((member.role_id ?? null) === roleId) return member;
        const result = await assignMemberRole(member.id, roleId);
        return { ...member, role_id: result.role_id, effective_features: result.effective_features };
      };

      if (editingId !== null) {
        const updated = await applyRole(await updateTeamMember(editingId, payload));
        setMembers(prev => prev.map(m => m.id === editingId ? updated : m));
      } else {
        const created = await applyRole(await addTeamMember(payload));
        setMembers(prev => [created, ...prev]);
      }
      setShowForm(false);
      resetForm();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to save this team member right now.';
      setFormError(message);
    } finally { setSaving(false); }
  };

  const handleAssignRole = async (m: TeamMember, nextRoleId: number | null) => {
    setAssigningRoleFor(m.id);
    try {
      const result = await assignMemberRole(m.id, nextRoleId);
      setMembers(prev => prev.map(x => x.id === m.id
        ? { ...x, role_id: result.role_id, effective_features: result.effective_features }
        : x));
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Could not change that role.');
    } finally { setAssigningRoleFor(null); }
  };

  /**
   * What a member can actually use.
   *
   * Prefers the server's `effective_features`, and recomputes from the role only
   * as a fallback for a member object built before the server told us. Reading
   * `allowed_features` here would be wrong whenever a role is assigned.
   */
  const effectiveFeatures = (m: TeamMember): FeatureKey[] => {
    if (m.effective_features) return m.effective_features;
    if (m.role_id) return roles.find(r => r.id === m.role_id)?.allowed_features ?? [];
    return m.allowed_features;
  };

  const handleToggleActive = async (m: TeamMember) => {
    try {
      const updated = await updateTeamMember(m.id, { is_active: !m.is_active });
      setMembers(prev => prev.map(x => x.id === m.id ? updated : x));
    } catch { /* ignore */ }
  };

  const handleBulk = async (action: 'activate' | 'deactivate' | 'remove') => {
    if (selected.length === 0) return;
    if (action === 'remove' && !window.confirm(`Remove ${selected.length} member${selected.length === 1 ? '' : 's'} from the workspace? Their seats are withdrawn and they are signed out.`)) return;
    setBulkBusy(true); setBulkNote(null);
    try {
      const r = await bulkTeamAction(selected, action);
      const fresh = await listTeamMembers();
      setMembers(fresh);
      setSelected([]);
      const skipped = r.skipped.length ? ` · skipped ${r.skipped.length}: ${r.skipped.map(x => x.reason).join(', ')}` : '';
      setBulkNote(`${action === 'remove' ? 'Removed' : action === 'activate' ? 'Activated' : 'Deactivated'} ${r.done.length}${skipped}`);
    } catch (e) { setBulkNote(e instanceof Error ? e.message : 'Bulk action failed'); }
    finally { setBulkBusy(false); }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await deleteTeamMember(id);
      setMembers(prev => prev.filter(m => m.id !== id));
    } catch { /* ignore */ } finally { setDeletingId(null); }
  };

  const handleCopyTemporaryPassword = async (memberId: number, password: string) => {
    try {
      await navigator.clipboard.writeText(password);
      setCopiedPasswordForId(memberId);
      window.setTimeout(() => {
        setCopiedPasswordForId((current) => (current === memberId ? null : current));
      }, 1600);
    } catch {
      window.prompt('Copy temporary password:', password);
    }
  };

  const handleGenerateTemporaryPassword = async (memberId: number) => {
    setResettingPasswordForId(memberId);
    try {
      const updated = await resetTeamMemberPassword(memberId);
      setMembers(prev => prev.map(member => member.id === memberId ? updated : member));
    } catch {
      // ignore for now; the UI already preserves the existing card state
    } finally {
      setResettingPasswordForId(null);
    }
  };

  const stats = useMemo(() => {
    const active = members.filter(m => m.is_active).length;
    const inactive = members.length - active;
    const featureCounts = ALL_FEATURES.reduce((acc, f) => {
      acc[f] = members.filter(m => m.allowed_features.includes(f)).length;
      return acc;
    }, {} as Record<FeatureKey, number>);
    const totalAssignments = members.reduce((acc, member) => acc + member.allowed_features.length, 0);
    const averageFeatureAccess = members.length ? Math.round(totalAssignments / members.length) : 0;
    const coveragePercent = members.length ? Math.round((totalAssignments / (members.length * ALL_FEATURES.length)) * 100) : 0;
    const topFeature = ALL_FEATURES.reduce((best, feature) => (
      featureCounts[feature] > featureCounts[best] ? feature : best
    ), ALL_FEATURES[0]);
    return {
      total: members.length,
      active,
      inactive,
      featureCounts,
      averageFeatureAccess,
      coveragePercent,
      topFeature,
    };
  }, [members]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <Eyebrow tone="default" dot>WORKSPACE</Eyebrow>
          <PageHeading
            accentWord="Access"
            accentTone="orange"
            size="h2"
            align="left"
            level="h2"
            className={styles.titlePlain}
          >
            Team Access
          </PageHeading>
          <p className={styles.subtitlePlain}>Invite teammates, manage permissions, and monitor workspace coverage from one place.</p>
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.secondaryBtnLight}
            onClick={() => setShowRoles(v => !v)}
          >
            {showRoles ? 'Hide roles' : `Manage roles (${roles.length})`}
          </button>
          {showForm && (
            <button
              type="button"
              className={styles.secondaryBtnLight}
              onClick={() => { setShowForm(false); resetForm(); }}
            >
              Close editor
            </button>
          )}
          <button
            className={styles.addBtn}
            onClick={openAdd}
            disabled={seatLimitReached}
            title={seatLimitReached ? seatLimitMessage : 'Add a new team member'}
          >
            <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/></svg>
            {seatLimitReached ? 'Member limit reached' : 'Add Member'}
          </button>
        </div>
      </div>

      {showRoles && (
        <RolesPanel
          roles={roles}
          members={members}
          onChange={setRoles}
          onMembersChanged={(updater) => setMembers(updater)}
        />
      )}

      {seatLimitReached && (
        <div className={styles.seatLimitBanner} role="status">
          {seatLimitMessage}
        </div>
      )}

      {/* Overview */}
      {!loading && members.length > 0 && (
        <>
          {/* First row - Stats cards */}
          <div className={styles.statsGrid}>
            <div className={styles.overviewCard}>
              <div className={styles.overviewIcon} style={{ background: 'var(--ds-info-bg)', color: 'var(--ds-link)' }}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18"><path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/></svg>
              </div>
              <div>
                <div className={styles.overviewValue}>{stats.total}</div>
                <div className={styles.overviewLabel}>Total Members</div>
              </div>
            </div>
            <div className={styles.overviewCard}>
              <div className={styles.overviewIcon} style={{ background: 'var(--ds-success-bg)', color: 'var(--ds-success-text)' }}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
              </div>
              <div>
                <div className={styles.overviewValue}>{stats.active}</div>
                <div className={styles.overviewLabel}>Active</div>
              </div>
            </div>
            <div className={styles.overviewCard}>
              <div className={styles.overviewIcon} style={{ background: 'var(--ds-info-bg)', color: 'var(--ds-info-text)' }}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18"><path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524L13.477 14.89zm1.414-1.414L6.524 5.11A6 6 0 0114.89 13.476zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd"/></svg>
              </div>
              <div>
                <div className={styles.overviewValue}>{stats.inactive}</div>
                <div className={styles.overviewLabel}>Inactive</div>
              </div>
            </div>
            <div className={styles.overviewCard}>
              <div className={styles.overviewIcon} style={{ background: 'var(--ds-info-bg)', color: 'var(--ds-link)' }}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18"><path d="M10 2a1 1 0 011 1v1.07A7.002 7.002 0 0117 11a7 7 0 11-8-6.93V3a1 1 0 011-1zm1 4.09V11a1 1 0 001 1h4.91A5.002 5.002 0 0011 6.09z"/></svg>
              </div>
              <div>
                <div className={styles.overviewValue}>{stats.coveragePercent}%</div>
                <div className={styles.overviewLabel}>Coverage Score</div>
                <div className={styles.kpiHint}>{stats.averageFeatureAccess} features per member</div>
              </div>
            </div>
          </div>

          {/* Second row - Feature Usage and Team cards */}
          <div className={styles.secondaryGrid}>
            <div className={`${styles.overviewCard} ${styles.overviewFeatureCard}`}>
              <div className={styles.panelHeader}>
                <div>
                  <div className={styles.overviewFeatureTitle}>Feature Usage</div>
                  <div className={styles.panelSubtext}>See how workspace access is distributed across the team.</div>
                </div>
                <span className={styles.panelBadge}>{stats.coveragePercent}% coverage</span>
              </div>
              <div className={styles.featureTable}>
                <div className={styles.featureTableHeader}>
                  <span>Feature</span>
                  <span>Enabled</span>
                  <span>Coverage</span>
                </div>
                {ALL_FEATURES.map(f => {
                  const count = stats.featureCounts[f];
                  const percent = stats.total ? Math.round((count / stats.total) * 100) : 0;
                  return (
                    <div key={f} className={styles.featureTableRow}>
                      <span className={styles.featureNameCell} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{FEATURE_ICONS[f]} {FEATURE_LABELS[f]}</span>
                      <span className={styles.featureCountCell}>{count}/{stats.total}</span>
                      <span className={styles.featurePercentCell}>{percent}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={`${styles.overviewCard} ${styles.overviewAvatarCard}`}>
              <div className={styles.panelHeader}>
                <div>
                  <div className={styles.overviewFeatureTitle}>Team Snapshot</div>
                  <div className={styles.panelSubtext}>Quick view of active seats and availability.</div>
                </div>
                <span className={styles.panelBadge}>{FEATURE_LABELS[stats.topFeature]}</span>
              </div>
              <div className={styles.avatarStack}>
                {members.slice(0, 8).map((m, i) => (
                  <div key={m.id} className={styles.avatarStackItem} style={{ zIndex: 10 - i, marginLeft: i === 0 ? 0 : -10 }} title={m.name}>
                    <AvatarSVG name={m.name} size={34} />
                  </div>
                ))}
                {members.length > 8 && (
                  <div className={styles.avatarStackMore}>+{members.length - 8}</div>
                )}
              </div>
              <div className={styles.avatarStackSub}>{stats.active} active · {stats.inactive} inactive</div>
            </div>
          </div>
          <SsoPanel />
          <DashboardConfigPanel />
          <TeamUsagePanel />
          <TeamActivityPanel />
        </>
      )}

      {/* Add / Edit Form */}
      {showForm && (
        <div className={styles.formCard}>
          <div className={styles.formTitle}>{editingId !== null ? 'Edit Member' : 'Add Team Member'}</div>

          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Full Name *</label>
              <input className={styles.formInput} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Priya Sharma" />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Employee ID</label>
              <input className={styles.formInput} value={employeeId} onChange={e => setEmployeeId(e.target.value)} placeholder="e.g. EMP-001" />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Email Address *</label>
              <input className={styles.formInput} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="e.g. priya@company.com" />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Position / Role</label>
              <input className={styles.formInput} value={position} onChange={e => setPosition(e.target.value)} placeholder="e.g. Compliance Analyst" />
            </div>
          </div>

          <div className={styles.formField} style={{ marginBottom: 18 }}>
            <label className={styles.formLabel}>Tags <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--ds-text-faint)' }}>(optional — press Enter to add)</span></label>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <input
                className={styles.formInput}
                style={{ flex: 1 }}
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="e.g. legal, india, privacy"
              />
              <button type="button" className={styles.tagAddBtn} onClick={addTag}>Add</button>
            </div>
            {tags.length > 0 && (
              <div className={styles.tagRow}>
                {tags.map(t => (
                  <span key={t} className={styles.tagChip}>
                    {t}
                    <button type="button" onClick={() => setTags(prev => prev.filter(x => x !== t))}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className={styles.featuresSection}>
            <label className={styles.formLabel}>Role</label>
            <p className={styles.featuresHint}>
              Assign a role to grant a ready-made set of features. Changing the role later
              updates everyone on it at once.
            </p>
            <select
              className={styles.roleSelect}
              value={roleId ?? ''}
              onChange={e => setRoleId(e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">No role — choose features individually</option>
              {roles.map(r => (
                <option key={r.id} value={r.id}>
                  {r.name}{r.is_system ? '' : ' (custom)'} — {r.allowed_features.length} features
                </option>
              ))}
            </select>
            {selectedRole && (
              <p className={styles.roleNote}>
                {selectedRole.description}
              </p>
            )}
          </div>

          <div className={styles.featuresSection}>
            <label className={styles.formLabel}>Feature Access</label>
            <p className={styles.featuresHint}>
              {selectedRole
                ? `Set by the ${selectedRole.name} role. Clear the role above to choose features individually.`
                : 'Toggle which features this member can access.'}
            </p>
            <div className={styles.featuresGrid}>
              {ALL_FEATURES.map(f => {
                // A role governs while assigned, so the grid shows what the role
                // grants rather than the member's stored list — otherwise it would
                // display access the member does not have.
                const enabled = selectedRole
                  ? selectedRole.allowed_features.includes(f)
                  : allowedFeatures.includes(f);
                const parentKey = FEATURE_CHILDREN[f];
                const isChild = !!parentKey;
                const parentEnabled = parentKey
                  ? (selectedRole ? selectedRole.allowed_features.includes(parentKey)
                                  : allowedFeatures.includes(parentKey))
                  : true;
                return (
                  <button
                    key={f}
                    type="button"
                    className={`${styles.featureToggle} ${enabled ? styles.featureOn : styles.featureOff} ${isChild ? styles.featureChild : ''}`}
                    onClick={() => toggleFeature(f)}
                    disabled={!!selectedRole || (isChild && !parentEnabled)}
                    title={selectedRole
                      ? `Granted by the ${selectedRole.name} role`
                      : (isChild && !parentEnabled ? `Enable ${FEATURE_LABELS[parentKey!]} first` : undefined)}
                  >
                    <span className={styles.featureToggleDot} />
                    <span>
                      {FEATURE_LABELS[f]}
                      {isChild && <span className={styles.featureChildNote}>{FEATURE_GROUP_LABEL[f]}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {formError && (
            <div className={styles.formErrorBanner} role="alert">
              {formError}
            </div>
          )}

          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => { setShowForm(false); resetForm(); }}>Cancel</button>
            <button
              className={styles.saveBtn}
              onClick={handleSave}
              disabled={saving || (editingId === null && seatLimitReached) || !name.trim() || !email.trim()}
            >
              {saving ? 'Saving…' : editingId !== null ? 'Save Changes' : seatLimitReached ? 'Limit reached' : 'Add Member'}
            </button>
          </div>
        </div>
      )}

      {/* Member list */}
      {loading ? (
        <div className={styles.empty}>Loading…</div>
      ) : members.length === 0 ? (
        <div className={styles.emptyState}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48" style={{ color: 'var(--ds-border-strong)' }}>
            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
          </svg>
          <p>No team members yet.</p>
          <p className={styles.emptyHint}>
            You manage this workspace as its owner. Add the people who will use it: each gets their own sign-in
            and only the features you allow.
          </p>
          <button className={styles.addBtn} onClick={openAdd}>+ Add your first member</button>
        </div>
      ) : (
        <div className={styles.directorySection}>
          <div className={styles.directoryHeader}>
            <div>
              <h3 className={styles.directoryTitle}>Member Directory</h3>
              <p className={styles.directorySubtitle}>Review seats, statuses, and permissions for every teammate.</p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', fontSize: 12.5 }}>
              <label style={{ color: 'var(--ds-text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="checkbox" checked={selected.length === members.length && members.length > 0}
                       onChange={e => setSelected(e.target.checked ? members.map(m => m.id) : [])} /> select all
              </label>
              {selected.length > 0 && (
                <>
                  <span style={{ color: 'var(--ds-text-muted)' }}>{selected.length} selected</span>
                  <button type="button" className={styles.iconBtn} disabled={bulkBusy} onClick={() => handleBulk('activate')}>Activate</button>
                  <button type="button" className={styles.iconBtn} disabled={bulkBusy} onClick={() => handleBulk('deactivate')}>Deactivate</button>
                  <button type="button" className={styles.iconBtn} disabled={bulkBusy} onClick={() => handleBulk('remove')} style={{ color: 'var(--ds-danger-text)' }}>Remove</button>
                </>
              )}
              {bulkNote && <span style={{ color: 'var(--ds-text-muted)' }}>{bulkNote}</span>}
            </div>
          </div>
          <div className={styles.memberList}>
            {members.map(m => (
              <div key={m.id} className={`${styles.memberCard} ${!m.is_active ? styles.memberInactive : ''}`}>
                <div className={styles.memberMain}>
                <input type="checkbox" aria-label={`Select ${m.name}`} checked={selected.includes(m.id)}
                       onChange={e => setSelected(prev => e.target.checked ? [...prev, m.id] : prev.filter(x => x !== m.id))}
                       style={{ marginRight: 8, alignSelf: 'center' }} />
                <AvatarSVG name={m.name} size={40} />
                <div className={styles.memberInfo}>
                  <div className={styles.memberName}>
                    {m.name}
                    {!m.is_active && <span className={styles.inactiveBadge}>Inactive</span>}
                  </div>
                  <div className={styles.memberMeta}>
                    {m.position && <span>{m.position}</span>}
                    {m.position && <span className={styles.dot}>·</span>}
                    <span>{m.email}</span>
                    {m.employee_id && <><span className={styles.dot}>·</span><span className={styles.empId}>#{m.employee_id}</span></>}
                  </div>
                  <div className={styles.memberQuickStats}>
                    <span className={styles.memberQuickStat}>{effectiveFeatures(m).length} features enabled</span>
                    <span className={styles.memberQuickStat}>{m.is_active ? 'Active seat' : 'Inactive seat'}</span>
                    <select
                      className={styles.memberRoleSelect}
                      value={m.role_id ?? ''}
                      disabled={assigningRoleFor === m.id}
                      onChange={e => handleAssignRole(m, e.target.value === '' ? null : Number(e.target.value))}
                      aria-label={`Role for ${m.name}`}
                    >
                      <option value="">No role</option>
                      {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                  <div className={styles.memberTempPasswordCard}>
                    <div className={styles.memberTempPasswordHeader}>
                      <span className={styles.memberTempPasswordLabel}>
                        Temporary password
                        {m.login_enabled && !m.temporary_password && (
                          <span style={{ marginLeft: 6, fontWeight: 400, color: 'var(--ds-text-muted)', fontSize: 11 }}>· Login already enabled</span>
                        )}
                      </span>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {m.temporary_password && (
                          <button
                            type="button"
                            className={styles.memberTempPasswordCopy}
                            onClick={() => handleCopyTemporaryPassword(m.id, m.temporary_password!)}
                          >
                            {copiedPasswordForId === m.id ? 'Copied' : 'Copy'}
                          </button>
                        )}
                        <button
                          type="button"
                          className={styles.memberTempPasswordCopy}
                          onClick={() => handleGenerateTemporaryPassword(m.id)}
                          disabled={resettingPasswordForId === m.id}
                        >
                          {resettingPasswordForId === m.id ? 'Generating…' : m.temporary_password ? 'Regenerate' : 'Generate'}
                        </button>
                      </div>
                    </div>
                    {m.temporary_password
                      ? <code className={styles.memberTempPasswordValue}>{m.temporary_password}</code>
                      : <span style={{ fontSize: 12, color: 'var(--ds-text-faint)', fontStyle: 'italic' }}>Not generated yet</span>
                    }
                  </div>
                  {((m as any).tags?.length > 0) && (
                    <div className={styles.memberTagRow}>
                      {(m as any).tags.map((t: string) => <span key={t} className={styles.memberTag}>{t}</span>)}
                    </div>
                  )}
                </div>
                <div className={styles.memberActions}>
                  <button
                    className={styles.iconBtn}
                    title="Expand feature access"
                    onClick={() => setExpandedId(expandedId === m.id ? null : m.id)}
                  >
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"
                      style={{ transform: expandedId === m.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
                    </svg>
                  </button>
                  <button className={styles.iconBtn} title="Edit" onClick={() => openEdit(m)}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zm-2.207 2.207L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
                  </button>
                  <button
                    className={`${styles.iconBtn} ${m.is_active ? styles.iconBtnWarning : styles.iconBtnSuccess}`}
                    title={m.is_active ? 'Deactivate' : 'Activate'}
                    onClick={() => handleToggleActive(m)}
                  >
                    {m.is_active
                      ? <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524L13.477 14.89zm1.414-1.414L6.524 5.11A6 6 0 0114.89 13.476zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd"/></svg>
                      : <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                    }
                  </button>
                  <button
                    className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                    title="Delete"
                    disabled={deletingId === m.id}
                    onClick={() => handleDelete(m.id)}
                  >
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
                  </button>
                </div>
              </div>

              {/* Feature access pills — always visible summary. Shows effective
                  access so a member on a role is never displayed as having
                  features the role has taken away. */}
              <div className={styles.featurePills}>
                {ALL_FEATURES.map(f => (
                  <span key={f} className={`${styles.featurePill} ${effectiveFeatures(m).includes(f) ? styles.pillOn : styles.pillOff}`}>
                    {FEATURE_LABELS[f]}
                  </span>
                ))}
              </div>

              {/* Expanded inline editor */}
              {expandedId === m.id && (m.role_id ? (
                <div className={styles.roleLockNote}>
                  Access for {m.name} comes from the <strong>{roles.find(r => r.id === m.role_id)?.name ?? 'assigned'}</strong> role.
                  Set the role to “No role” above to edit their features individually,
                  or change the role itself to update everyone on it.
                </div>
              ) : (
                <InlineFeatureEditor member={m} onSave={async (features) => {
                  const updated = await updateTeamMember(m.id, { allowed_features: features });
                  setMembers(prev => prev.map(x => x.id === m.id ? updated : x));
                  setExpandedId(null);
                }} />
              ))}
            </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Create, edit and delete the roles an organisation can assign.
 *
 * Predefined roles are editable but not deletable — the product's guidance
 * assumes Admin/Legal/Compliance/Research/Viewer exist, while what each means
 * legitimately differs between organisations.
 */
function RolesPanel({ roles, members, onChange, onMembersChanged }: {
  roles: EnterpriseRole[];
  members: TeamMember[];
  onChange: (roles: EnterpriseRole[]) => void;
  onMembersChanged: (updater: (prev: TeamMember[]) => TeamMember[]) => void;
}) {
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftFeatures, setDraftFeatures] = useState<FeatureKey[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const memberCount = (roleId: number) => members.filter(m => m.role_id === roleId).length;

  const startNew = () => {
    setEditingId('new'); setDraftName(''); setDraftDescription('');
    setDraftFeatures([]); setError(null);
  };

  const startEdit = (role: EnterpriseRole) => {
    setEditingId(role.id); setDraftName(role.name);
    setDraftDescription(role.description ?? '');
    setDraftFeatures([...role.allowed_features]); setError(null);
  };

  const toggle = (f: FeatureKey) =>
    setDraftFeatures(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);

  const save = async () => {
    if (!draftName.trim()) { setError('Give the role a name.'); return; }
    setBusy(true); setError(null);
    try {
      if (editingId === 'new') {
        const created = await createRole({
          name: draftName.trim(),
          allowed_features: draftFeatures,
          description: draftDescription.trim() || undefined,
        });
        onChange([...roles, created]);
      } else if (editingId !== null) {
        const updated = await updateRole(editingId, {
          name: draftName.trim(),
          allowed_features: draftFeatures,
          description: draftDescription.trim(),
        });
        onChange(roles.map(r => r.id === updated.id ? updated : r));
        // Editing a role changes access for everyone on it, so the cards below
        // must reflect that immediately rather than after a reload.
        onMembersChanged(prev => prev.map(m => m.role_id === updated.id
          ? { ...m, effective_features: updated.allowed_features }
          : m));
      }
      setEditingId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that role.');
    } finally { setBusy(false); }
  };

  const remove = async (role: EnterpriseRole) => {
    const count = memberCount(role.id);
    const warning = count
      ? `Delete “${role.name}”? ${count} member${count === 1 ? '' : 's'} will lose the access it grants.`
      : `Delete “${role.name}”?`;
    if (!window.confirm(warning)) return;
    setBusy(true); setError(null);
    try {
      await deleteRole(role.id);
      onChange(roles.filter(r => r.id !== role.id));
      // Members fall back to their own feature list once the role is gone.
      onMembersChanged(prev => prev.map(m => m.role_id === role.id
        ? { ...m, role_id: null, effective_features: m.allowed_features }
        : m));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete that role.');
    } finally { setBusy(false); }
  };

  return (
    <div className={styles.rolesPanel}>
      <div className={styles.rolesPanelHeader}>
        <div>
          <h3 className={styles.rolesPanelTitle}>Roles</h3>
          <p className={styles.rolesPanelHint}>
            A role is a named set of features. Change the role once and everyone on it changes with it.
          </p>
        </div>
        <button type="button" className={styles.addBtn} onClick={startNew} disabled={busy}>
          + New role
        </button>
      </div>

      {error && <div className={styles.formErrorBanner} role="alert">{error}</div>}

      <div className={styles.rolesList}>
        {roles.map(role => (
          <div key={role.id} className={styles.roleRow}>
            <div className={styles.roleRowMain}>
              <span className={styles.roleName}>
                {role.name}
                {role.is_system && <span className={styles.roleSystemTag}>predefined</span>}
              </span>
              {role.description && <span className={styles.roleDescription}>{role.description}</span>}
              <span className={styles.roleCounts}>
                {role.allowed_features.length} features · {memberCount(role.id)} member{memberCount(role.id) === 1 ? '' : 's'}
              </span>
            </div>
            <div className={styles.roleRowActions}>
              <button type="button" className={styles.secondaryBtnLight}
                      onClick={() => startEdit(role)} disabled={busy}>Edit</button>
              {!role.is_system && (
                <button type="button" className={styles.secondaryBtnLight}
                        onClick={() => remove(role)} disabled={busy}>Delete</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {editingId !== null && (
        <div className={styles.roleEditor}>
          <label className={styles.formLabel}>{editingId === 'new' ? 'New role' : 'Edit role'}</label>
          <input
            className={styles.formInput}
            value={draftName}
            onChange={e => setDraftName(e.target.value)}
            placeholder="Role name, e.g. Auditor"
          />
          <input
            className={styles.formInput}
            value={draftDescription}
            onChange={e => setDraftDescription(e.target.value)}
            placeholder="What this role is for (optional)"
          />
          <div className={styles.featuresGrid}>
            {ALL_FEATURES.map(f => (
              <button
                key={f}
                type="button"
                className={`${styles.featureToggle} ${draftFeatures.includes(f) ? styles.featureOn : styles.featureOff}`}
                onClick={() => toggle(f)}
              >
                <span className={styles.featureToggleDot} />
                <span>{FEATURE_LABELS[f]}</span>
              </button>
            ))}
          </div>
          <div className={styles.roleEditorActions}>
            <button type="button" className={styles.addBtn} onClick={save} disabled={busy}>
              {busy ? 'Saving…' : 'Save role'}
            </button>
            <button type="button" className={styles.secondaryBtnLight}
                    onClick={() => setEditingId(null)} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function InlineFeatureEditor({ member, onSave }: { member: TeamMember; onSave: (f: FeatureKey[]) => Promise<void> }) {
  const [features, setFeatures] = useState<FeatureKey[]>([...member.allowed_features]);
  const [saving, setSaving] = useState(false);

  const toggle = (f: FeatureKey) => {
    setFeatures(prev => {
      const next = prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f];
      if (f === 'chat') return next.includes('chat') ? (next.includes('chat-history') ? next : [...next, 'chat-history' as FeatureKey]) : next.filter(x => x !== 'chat-history');
      if (f === 'search') return next.includes('search') ? (next.includes('search-history') ? next : [...next, 'search-history' as FeatureKey]) : next.filter(x => x !== 'search-history');
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try { await onSave(features); } catch { /* ignore */ } finally { setSaving(false); }
  };

  return (
    <div className={styles.inlineEditor}>
      <div className={styles.inlineEditorTitle}>Edit Feature Access</div>
      <div className={styles.featuresGrid}>
        {ALL_FEATURES.map(f => {
          const on = features.includes(f);
          const parentKey = FEATURE_CHILDREN[f];
          const isChild = !!parentKey;
          const parentEnabled = parentKey ? features.includes(parentKey) : true;
          return (
            <button key={f} type="button"
              className={`${styles.featureToggle} ${on ? styles.featureOn : styles.featureOff} ${isChild ? styles.featureChild : ''}`}
              onClick={() => toggle(f)}
              disabled={isChild && !parentEnabled}
            >
              <span className={styles.featureToggleDot} />
              <span>
                {FEATURE_LABELS[f]}
                {isChild && <span className={styles.featureChildNote}>{FEATURE_GROUP_LABEL[f]}</span>}
              </span>
            </button>
          );
        })}
      </div>
      <div className={styles.formActions}>
        <button className={styles.saveBtn} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save Access'}
        </button>
      </div>
    </div>
  );
}
